import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const bgImage = 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20Venezuelan%20oil%20field%20at%20sunset%20with%20multiple%20pump%20jacks%20silhouetted%20against%20deep%20orange%20and%20crimson%20sky%2C%20industrial%20scale%20petroleum%20production%20infrastructure%2C%20epic%20cinematic%20wide%20angle%2C%20warm%20dramatic%20tones%2C%20dust%20particles%20in%20air%2C%20moody%20atmospheric%20lighting%2C%20high%20contrast%20professional%20photography&width=1800&height=900&seq=nosotros-hero-01&orientation=landscape';

export default function NosotrosHero() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section ref={ref} className="relative w-full min-h-[85vh] flex items-center overflow-hidden">
      {/* Static background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={bgImage}
          alt="Poly Alphas Chemicals - Operaciones petroleras Venezuela"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Double overlay: base dark + directional gradient */}
        <div className="absolute inset-0 bg-foreground-950/65" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/88 to-foreground-950/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30" />
      </div>

      {/* Decorative top line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 via-accent-500 to-transparent z-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-24 w-full">
        <div className="flex flex-col lg:flex-row gap-16 items-center">

          {/* Left content */}
          <div className="flex-1 max-w-2xl">
            <div className={`flex items-center gap-3 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="w-1.5 h-12 rounded-full bg-accent-500" />
              <span className="text-accent-400 text-xs font-semibold tracking-widest uppercase">
                Quiénes Somos
              </span>
            </div>

            <h1 className={`font-heading font-bold text-5xl md:text-7xl text-white leading-tight mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Más de{' '}
              <span className="text-accent-400">30 años</span>{' '}
              haciendo historia
            </h1>

            <p className={`text-foreground-200 text-lg md:text-xl leading-relaxed mb-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              Somos <strong className="text-white">Poly Alphas Chemicals C.A.</strong> — la empresa
              venezolana especializada en química para la industria petrolera con mayor trayectoria
              en producción, perforación y tratamiento de fluidos.
            </p>

            <div className={`flex flex-wrap gap-4 mb-12 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <Link
                to="/catalogo"
                className="inline-flex items-center gap-2 px-7 py-4 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-book-open-line"></i>
                Ver Nuestros Productos
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-7 py-4 border border-white/40 text-white hover:bg-white/15 font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-chat-1-line"></i>
                Hablar con un Técnico
              </Link>
            </div>

            {/* Trust row */}
            <div className={`flex flex-wrap gap-3 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {[
                { icon: 'ri-map-pin-line', text: 'Venezuela' },
                { icon: 'ri-flask-line', text: 'Laboratorio propio' },
                { icon: 'ri-shield-check-line', text: 'Certificados PDVSA' },
                { icon: 'ri-truck-line', text: 'Distribución nacional' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/12 border border-white/20 backdrop-blur-sm">
                  <i className={`${b.icon} text-accent-400 text-sm`}></i>
                  <span className="text-white text-sm font-medium">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: big stats */}
          <div className={`w-full lg:w-auto flex-shrink-0 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '30+', label: 'Años de trayectoria', icon: 'ri-award-fill', color: 'text-accent-400' },
                { value: '50+', label: 'Productos en catálogo', icon: 'ri-flask-fill', color: 'text-primary-400' },
                { value: '200+', label: 'Clientes activos', icon: 'ri-group-fill', color: 'text-accent-400' },
                { value: '100%', label: 'Made in Venezuela', icon: 'ri-map-pin-fill', color: 'text-primary-400' },
              ].map((s) => (
                <div key={s.label} className="p-6 rounded-2xl bg-white/12 border border-white/20 backdrop-blur-sm text-center flex flex-col items-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15">
                    <i className={`${s.icon} ${s.color} text-xl`}></i>
                  </div>
                  <span className={`font-heading font-bold text-4xl ${s.color}`}>{s.value}</span>
                  <span className="text-white/85 text-xs text-center leading-tight">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent z-10" />
    </section>
  );
}